import { useEffect, useRef } from "react";
import { ShoppingCart, Store, X } from "lucide-react";
import type { EnrichedOrder } from "@/features/order/types";
import { getOrderTotal } from "@/features/quotation/clientDisplay";
import { formatBRL } from "@/lib/timeAgo";

export function FinalizeOrderDrawer({
  open,
  orders,
  storeNameById,
  onClose,
  onOpenOrder,
}: {
  open: boolean;
  orders: EnrichedOrder[];
  storeNameById: Map<string, string>;
  onClose: () => void;
  onOpenOrder: (orderId: string) => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const returnFocus = document.activeElement as HTMLElement | null;
    dialogRef.current
      ?.querySelector<HTMLButtonElement>(".lnk-modal-close")
      ?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const buttons = Array.from(
        dialogRef.current.querySelectorAll<HTMLButtonElement>(
          "button:not(:disabled)",
        ),
      );
      const first = buttons[0];
      const last = buttons.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      returnFocus?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lnk-sheet-scrim is-open" onClick={onClose}>
      <section
        ref={dialogRef}
        className="lnk-orders-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Pedidos em andamento"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="lnk-kicker">PEDIDOS EM ANDAMENTO</span>
            <h2>Escolha uma loja</h2>
          </div>
          <button
            className="lnk-modal-close"
            aria-label="Fechar"
            onClick={onClose}
          >
            <X size={19} />
          </button>
        </header>
        <div
          className="lnk-order-list"
          data-testid="finalize-selector-list"
          data-scrollable="true"
        >
          {orders.map((order) => (
            <button
              key={order.id}
              className="lnk-order-row finalize-selector-row"
              onClick={() => onOpenOrder(order.id)}
            >
              <span className="lnk-order-icon">
                <Store size={18} />
              </span>
              <span>
                <strong>
                  {order.store_name ||
                    storeNameById.get(order.store_id) ||
                    "Loja sem nome"}
                </strong>
                <small>{order.items.length} itens</small>
              </span>
              <b>{formatBRL(getOrderTotal(order))}</b>
              <span className="lnk-review-label">Revisar</span>
            </button>
          ))}
        </div>
        <footer>
          <span>
            <strong>
              {orders.length} {orders.length === 1 ? "pedido" : "pedidos"}
            </strong>
            <small>TOTAL COMBINADO</small>
          </span>
          <b>
            {formatBRL(
              orders.reduce((total, order) => total + getOrderTotal(order), 0),
            )}
          </b>
        </footer>
      </section>
    </div>
  );
}

export function FinalizeOrderCta({
  disabled,
  storeLabel,
  totalLabel,
  statusLabel,
  dateLabel,
  onClick,
}: {
  disabled: boolean;
  storeLabel?: string;
  totalLabel?: string;
  statusLabel?: string;
  dateLabel?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="lnk-orders-float finalize-cta-button"
      data-testid="finalize-cluster"
      aria-disabled={disabled}
      aria-label="Finalizar pedido"
      onClick={onClick}
    >
      <span className="lnk-cart-icon">
        <ShoppingCart size={20} />
      </span>
      <span>
        <small>
          {storeLabel ? `${storeLabel} · ${dateLabel}` : statusLabel}
        </small>
        <strong>{totalLabel ?? statusLabel}</strong>
      </span>
      <b>Revisar pedidos</b>
    </button>
  );
}
